import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { OrganizationWithHierarchyType } from '../../contexts/types';
import OrgChartSelectModal from './OrgChartSelectModal';
import { useAppSelector } from '../../store/hooks';
import HelpIcon from '../InvestmentScreening/HelpIcon';
import FilterTextBox from '../../components/FilterTextBox';
import { formatLocalDateTime } from '../../utils/utils';

/** テーブル表示・API送受信に使う制限組織の型 */
type RestrictedOrg = {
  organizationId: string;
  organizationName: string;
  /** 登録日時（updated_at の整形済み文字列 "YYYY/MM/DD HH:mm:ss"） */
  addedAt: string;
  /** 登録者（updated_by: 再登録時は最後に登録した人が入る） */
  createdBy: string;
};

const AIRestrictedOrganization: React.FC = () => {
  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const toast = useToast();
  /** 確認モーダルの作成者列に表示するログインユーザー名（未使用だが将来の拡張用に保持） */
  const employeeName = useAppSelector(state => state.employee.employeeName);

  /** 組織ツリー全件（SVGモーダルの描画と子孫ID取得に使用） */
  const [allOrgs, setAllOrgs] = useState<OrganizationWithHierarchyType[]>([]);
  /** 現在制限登録されている組織の一覧（テーブル表示用） */
  const [restrictedOrgs, setRestrictedOrgs] = useState<RestrictedOrg[]>([]);
  /** テーブルの子組織を展開表示している組織IDのSet */
  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<string>>(new Set());
  /** 事業部名フィルターの入力値 */
  const [filterText, setFilterText] = useState('');

  /** 組織選択モーダル（OrgChartSelectModal）の開閉 */
  const { isOpen: isChartOpen, onOpen: onChartOpen, onClose: onChartClose } = useDisclosure();
  /** 解除確認ダイアログの開閉 */
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  /** 保存確認ダイアログの開閉 */
  const { isOpen: isSaveConfirmOpen, onOpen: onSaveConfirmOpen, onClose: onSaveConfirmClose } = useDisclosure();

  /** 解除確認ダイアログで「キャンセル」ボタンにフォーカスを当てるための ref（AlertDialog 必須） */
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  /** 保存確認ダイアログで「キャンセル」ボタンにフォーカスを当てるための ref（AlertDialog 必須） */
  const cancelSaveRef = useRef<HTMLButtonElement>(null);
  /**
   * 保存確認ダイアログが閉じた後にフォーカスを戻す先（「事業部を追加」ボタン）。
   * onChartClose() と onSaveConfirmOpen() を同一ハンドラで連続呼び出しすると
   * Chakra UI がフォーカス先を正しくキャプチャできないため、finalFocusRef で明示指定する。
   */
  const addButtonRef = useRef<HTMLButtonElement>(null);
  /** 解除対象の組織ID（解除確認ダイアログを開く際にセット、確定後にクリア） */
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  /**
   * 組織選択モーダルで「保存」を押した後、保存確認ダイアログが開くまでの間に
   * 選択済み組織IDを一時保持するステート。
   * 確認ダイアログ内で「追加」「解除」の差分表示にも使用する。
   */
  const [pendingAddedIds, setPendingAddedIds] = useState<string[]>([]);

  useEffect(() => {
    fetchOrgs();
    fetchRestrictedOrgs();
  }, []);

  /** 組織ツリー全件を取得して allOrgs にセット */
  const fetchOrgs = async () => {
    const payload = {
      query: `
        query getOrganizationWithHierarchy {
          getOrganizationWithHierarchy {
            organizationId
            organizationName
            parentOrganizationId
            parentOrganizationName
          }
        }
      `,
    };
    const result = await WebAPI.request(payload);
    const orgs: OrganizationWithHierarchyType[] = result?.data?.getOrganizationWithHierarchy ?? [];
    setAllOrgs(orgs);
  };

  /** 制限登録済み組織の一覧を API から取得して restrictedOrgs にセット */
  const fetchRestrictedOrgs = async () => {
    try {
      const result = await WebAPI.request({
        query: `
          query getAIRestrictedOrganizations {
            getAIRestrictedOrganizations {
              organizationId
              organizationName
              createdAt
              createdBy
            }
          }
        `,
      });
      const orgs = result?.data?.getAIRestrictedOrganizations ?? [];
      setRestrictedOrgs(orgs.map((o: { organizationId: string; organizationName: string; createdAt: string; createdBy: string }) => ({
        organizationId: o.organizationId,
        organizationName: o.organizationName,
        addedAt: formatLocalDateTime(o.createdAt),   // updated_at ベース、ロケールに応じて整形
        createdBy: o.createdBy, // updated_by ベース（再登録時は最後の登録者）
      })));
    } catch (error) {
      console.error('fetchRestrictedOrgs error:', error);
    }
  };

  /**
   * 指定した組織IDの全子孫IDを再帰的に収集する。
   * 親組織を登録する際に配下の既登録エントリを自動削除するために使用。
   */
  const getDescendantIds = (orgId: string): string[] => {
    const children = allOrgs.filter(o => o.parentOrganizationId === orgId);
    return children.flatMap(c => [c.organizationId, ...getDescendantIds(c.organizationId)]);
  };

  /**
   * 組織選択モーダルの「保存」ボタン押下時に呼ばれる。
   * モーダルを閉じ、選択済みIDを pendingAddedIds に保持してから保存確認ダイアログを開く。
   * @param newAddedIds モーダルで選択された組織IDの配列
   */
  const handleOrgSave = (newAddedIds: string[]) => {
    setPendingAddedIds(newAddedIds);
    onChartClose();
    onSaveConfirmOpen();
  };

  /**
   * 保存確認ダイアログの「保存」ボタン押下時に呼ばれる。
   * pendingToAdd（新規追加分）と pendingToRemove（解除分）をそれぞれ API に送信し、
   * 完了後に一覧を再取得する。
   */
  const handleSaveConfirm = async () => {
    try {
      const mutationQuery = `
        mutation putAIRestrictedOrganization($input: PutAIRestrictedOrganizationInput!) {
          putAIRestrictedOrganization(input: $input) { status message }
        }
      `;

      // 追加分: deleted: false（UPSERT）
      for (const id of pendingToAdd) {
        const result = await WebAPI.request({
          query: mutationQuery,
          variables: { input: { organizationId: id, deleted: false } },
        });
        if (result?.data?.putAIRestrictedOrganization?.status !== 'success') {
          throw new Error(`putAIRestrictedOrganization failed for ${id}`);
        }
      }

      // 解除分: deleted: true（論理削除）
      for (const org of pendingToRemove) {
        const result = await WebAPI.request({
          query: mutationQuery,
          variables: { input: { organizationId: org.organizationId, deleted: true } },
        });
        if (result?.data?.putAIRestrictedOrganization?.status !== 'success') {
          throw new Error(`putAIRestrictedOrganization failed for ${org.organizationId}`);
        }
      }

      await fetchRestrictedOrgs();
      setPendingAddedIds([]);
      onSaveConfirmClose();
      toast({
        // 保存成功
        title: t('features.MasterMaintenance.AIRestrictedOrganization.message.saveSuccess'),
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      // 保存失敗
      console.error('handleSaveConfirm error:', error);
      toast({
        title: t('features.MasterMaintenance.AIRestrictedOrganization.message.saveFailed'),
        status: 'error',
        duration: 3000,
      });
    }
  };

  /** テーブルの解除ボタン押下時: 対象IDをセットして解除確認ダイアログを開く */
  const handleDeleteClick = (organizationId: string) => {
    setDeleteTarget(organizationId);
    onDeleteOpen();
  };

  /** 解除確認ダイアログの「解除」ボタン押下時: API で論理削除して一覧を再取得する */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const result = await WebAPI.request({
        query: `
          mutation putAIRestrictedOrganization($input: PutAIRestrictedOrganizationInput!) {
            putAIRestrictedOrganization(input: $input) { status message }
          }
        `,
        variables: { input: { organizationId: deleteTarget, deleted: true } },
      });
      if (result?.data?.putAIRestrictedOrganization?.status !== 'success') {
        throw new Error(`putAIRestrictedOrganization failed for ${deleteTarget}`);
      }
      await fetchRestrictedOrgs();
      // 展開状態も合わせてクリア
      setExpandedOrgIds(prev => { const next = new Set(prev); next.delete(deleteTarget); return next; });
      setDeleteTarget(null);
      onDeleteClose();
    } catch (error) {
      console.error('handleDeleteConfirm error:', error);
      // 保存失敗
      toast({
        title: t('features.MasterMaintenance.AIRestrictedOrganization.message.saveFailed'),
        status: 'error',
        duration: 3000,
      });
    }
  };

  /** テーブル行の子組織ツリーを展開／折りたたむ */
  const toggleExpand = (orgId: string) => {
    setExpandedOrgIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId); else next.add(orgId);
      return next;
    });
  };

  /**
   * 制限登録済み組織の1行（＋展開時の子孫行）を再帰的に生成する。
   * @param orgId 描画対象の組織ID
   * @param depth インデント深さ（0始まり、1段あたり24px）
   * @param registeredOrg 制限登録情報（登録済みの場合のみ渡す。子孫展開行は null）
   */
  const renderOrgRows = (
    orgId: string,
    depth: number,
    registeredOrg: RestrictedOrg | null,
  ): React.ReactNode[] => {
    const orgData = allOrgs.find(o => o.organizationId === orgId);
    if (!orgData) return [];

    const children = allOrgs.filter(o => o.parentOrganizationId === orgId);
    const hasChildren = children.length > 0;
    const isExpanded = expandedOrgIds.has(orgId);
    const isRegistered = registeredOrg !== null;

    const row = (
      <Tr key={orgId} _hover={{ bgColor: 'gray.50' }}>
        <Td>
          <Flex align="center" pl={`${depth * 24}px`}>
            {hasChildren ? (
              <IconButton
                aria-label="toggle"
                icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                size="xs"
                variant="ghost"
                color="gray.500"
                minW="20px"
                h="20px"
                mr={1}
                onClick={() => toggleExpand(orgId)}
              />
            ) : (
              <Box minW="20px" mr={1} />
            )}
            {/* 制限登録済みの組織名は太字で強調 */}
            <Text fontWeight={isRegistered ? 'bold' : 'normal'} fontSize="sm">
              {orgData.organizationName}
            </Text>
          </Flex>
        </Td>
        <Td color="gray.500" fontSize="xs">{isRegistered ? orgId : ''}</Td>
        <Td fontSize="sm">{isRegistered && registeredOrg ? registeredOrg.addedAt : ''}</Td>
        <Td fontSize="sm">{isRegistered && registeredOrg ? registeredOrg.createdBy : ''}</Td>
        <Td>
          {isRegistered && (
            <Button
              size="xs"
              colorScheme="red"
              variant="outline"
              onClick={() => handleDeleteClick(orgId)}
            >
              {t('common.button.delete')}
            </Button>
          )}
        </Td>
      </Tr>
    );

    // 展開中の場合のみ子組織行を生成（子組織は registeredOrg=null で描画）
    const childRows: React.ReactNode[] = isExpanded
      ? children.flatMap(child => renderOrgRows(child.organizationId, depth + 1, null))
      : [];

    return [row, ...childRows];
  };

  /** 組織選択モーダルに渡す「既に登録済みの組織ID一覧」（チェック状態の初期化に使用） */
  const addedIds = restrictedOrgs.map(r => r.organizationId);

  /**
   * 保存確認ダイアログ用の差分計算。
   * pendingToAdd:    モーダルで新たに選択された組織（既存登録にない → 追加）
   * pendingToRemove: 既存登録にあるがモーダルの選択から外れた組織（→ 解除）
   */
  const pendingToAdd = pendingAddedIds.filter(id => !restrictedOrgs.some(r => r.organizationId === id));
  const pendingToRemove = restrictedOrgs.filter(r => !pendingAddedIds.includes(r.organizationId));

  /**
   * フィルタ・ソート済みの表示用一覧。
   * - 事業部名の前方一致（大文字小文字・全半角区別なし）でフィルタ
   * - 事業部名の昇順でソート（DB の ORDER BY と同じ方向だが、フロントで明示保証）
   */
  const filteredOrgs = [...restrictedOrgs]
    .sort((a, b) => a.organizationName.localeCompare(b.organizationName, 'ja'))
    .filter(org => org.organizationName.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <Box h="100%" display="flex" flexDirection="column" pt={4}>
      <Flex align="center" gap={2} mb={4}>
        <Button ref={addButtonRef} colorScheme="blue" onClick={onChartOpen} onKeyDown={(e) => { if (e.repeat) e.preventDefault(); }}>
          {t('features.MasterMaintenance.AIRestrictedOrganization.button.add')}
        </Button>
        <HelpIcon placement="bottom-start" width="auto">
          {t('features.MasterMaintenance.AIRestrictedOrganization.tooltip.description')}
        </HelpIcon>
      </Flex>

      <TableContainer flex={1} overflowY="auto" borderWidth={1} borderRadius="md" borderColor="gray.200">
        <Table size="sm">
          <Thead position="sticky" top={0} zIndex={1} bgColor="gray.50">
            <Tr>
              <Th>
                {t('features.MasterMaintenance.AIRestrictedOrganization.field.organizationName')}
                <FilterTextBox
                  placeholder={t('features.MasterMaintenance.AIRestrictedOrganization.placeholder.filter')}
                  filterValue={filterText}
                  setFilterValue={setFilterText}
                />
              </Th>
              <Th>{t('features.MasterMaintenance.AIRestrictedOrganization.field.organizationId')}</Th>
              <Th>{t('features.MasterMaintenance.AIRestrictedOrganization.field.addedAt')}</Th>
              <Th>{t('features.MasterMaintenance.AIRestrictedOrganization.field.createdBy')}</Th>
              <Th w="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {restrictedOrgs.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" color="gray.400" py={8}>
                  {t('features.MasterMaintenance.AIRestrictedOrganization.message.empty')}
                </Td>
              </Tr>
            ) : filteredOrgs.length === 0 ? (
              // 登録データはあるがフィルタ条件に一致しない場合
              <Tr>
                <Td colSpan={5} textAlign="center" color="gray.400" py={8}>
                  {t('features.MasterMaintenance.AIRestrictedOrganization.message.noFilterResult')}
                </Td>
              </Tr>
            ) : (
              filteredOrgs.flatMap(org => renderOrgRows(org.organizationId, 0, org))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* 組織選択モーダル: SVGツリーで組織を選択し、保存ボタンで handleOrgSave を呼ぶ */}
      <OrgChartSelectModal
        isOpen={isChartOpen}
        onClose={onChartClose}
        orgList={allOrgs}
        addedIds={addedIds}
        onSave={handleOrgSave}
      />

      {/* 保存確認モーダル: 追加・解除の差分を表示してから API 送信する */}
      <AlertDialog isOpen={isSaveConfirmOpen} leastDestructiveRef={cancelSaveRef} finalFocusRef={addButtonRef} onClose={onSaveConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {t('features.MasterMaintenance.AIRestrictedOrganization.modal.saveTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={2}>{t('features.MasterMaintenance.AIRestrictedOrganization.modal.saveConfirm')}</Text>
              {pendingToAdd.length > 0 && (
                <Box mb={2}>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    {t('features.MasterMaintenance.AIRestrictedOrganization.modal.saveAdd')}
                  </Text>
                  {pendingToAdd.map(id => {
                    const org = allOrgs.find(o => o.organizationId === id);
                    return (
                      <Text key={id} fontSize="sm" pl={3}>
                        ・{org?.organizationName ?? id}
                      </Text>
                    );
                  })}
                </Box>
              )}
              {pendingToRemove.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="red.500">
                    {t('features.MasterMaintenance.AIRestrictedOrganization.modal.saveRemove')}
                  </Text>
                  {pendingToRemove.map(org => (
                    <Text key={org.organizationId} fontSize="sm" pl={3}>
                      ・{org.organizationName}
                    </Text>
                  ))}
                </Box>
              )}
              {pendingToAdd.length === 0 && pendingToRemove.length === 0 && (
                <Text fontSize="sm" color="gray.500">
                  {t('features.MasterMaintenance.AIRestrictedOrganization.modal.noChange')}
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSaveRef} onClick={onSaveConfirmClose} onKeyDown={(e) => { if (e.repeat) e.preventDefault(); }}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme="blue" onClick={handleSaveConfirm} ml={3} onKeyDown={(e) => { if (e.repeat) e.preventDefault(); }}>
                {t('common.button.save')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 解除確認モーダル: テーブルの解除ボタンから開き、1件だけ論理削除する */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {t('features.MasterMaintenance.AIRestrictedOrganization.modal.deleteTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('features.MasterMaintenance.AIRestrictedOrganization.modal.deleteConfirm')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={onDeleteClose} onKeyDown={(e) => { if (e.repeat) e.preventDefault(); }}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3} onKeyDown={(e) => { if (e.repeat) e.preventDefault(); }}>
                {t('common.button.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default AIRestrictedOrganization;
